import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdDialog, MdDialogRef } from '@angular/material';
import { Observable } from 'rxjs/Observable';

import { ConstantsService } from 'app/services/constants-service';
import { FormErrors} from 'app/shared/validators/generic-validators';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { ApplicationTypeConstants, WarmUpService } from 'app/services/warmup/warmup.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';

import { Address } from "app/shared/models/address.model";
import { BankAccount } from "app/shared/models/bank-account.model";
import { BankInstitution } from 'app/shared/models/bank-institution.model';

import { FinanceService } from 'app/finance/finance.service';

@Component({
    selector: 'bankinstitution',
    templateUrl: './bankinstitution.component.html',
})

/**
 * Component for adding/editing a Bank Institution.
 */
export class BankInstitutionComponent implements OnInit {
    bankInstitutionForm: FormGroup;
    savingBankInstitution: boolean;
    selectedBankInstitution: BankInstitution;
    action: string;

    addressTypes: ApplicationValue[];
    bankAddress: Address;
    bankAccounts: BankAccount[];
    bankInstitutionId: number;
    countries: ApplicationValue[];

    formErrors = {
        'bankName': '',
        'bankCode': '',
        'addressType': '',
        'city': '',
        'country': ''
    };

    validationMessages = {
        'bankName': {
            'required': 'Required field value.',
        },
        'bankCode': {
            'required': 'Required field value.',
        },
        'addressType': {
            'required': 'Required field value.'
        },
        'city': {
            'required': 'Required field value.'
        },
        'country': {
            'required': 'Required field value.'
        }
    };

    constructor(
        public dialogRef: MdDialogRef<any>,
        private formBuilder: FormBuilder,
        private financeService: FinanceService,
        private snackBarService: SnackBarService,
        private trackingService: TrackingService,
        private warmUpService: WarmUpService) { }

    createForm() {
        this.bankInstitutionForm = this.formBuilder.group({
            bankName: ['', Validators.required],
            bankCode: [''],
            houseNumber: '',
            address: '',
            city: '',
            zipcode: '',
            addressType: ['', [Validators.required]],
            country: ['', [Validators.required]],
        });

        this.bankInstitutionForm.valueChanges.subscribe(data => this.onValueChanged(data));
        this.onValueChanged();
    }

    callSnackBarService(response: any, successMsg: string, failureMsg: string) {
        this.snackBarService.showStatus(response, successMsg, failureMsg);
    }

    getAddressTypes() {
        const addressTypes =
            this.warmUpService.getApplicationValue(ApplicationTypeConstants.ADDRESS_TYPE);
        if (addressTypes instanceof Observable) {
            addressTypes.subscribe(
                response => {
                    this.warmUpService.setApplicationTypeInLS(response);
                    this.addressTypes = response.values;
                }
            );
        } else {
            this.addressTypes = addressTypes;
        }
    }

    getCountries() {
        const countries =
            this.warmUpService.getApplicationValue(ApplicationTypeConstants.COUNTRY_TYPE);
        if (countries instanceof Observable) {
            countries.subscribe(
                response => {
                    this.warmUpService.setApplicationTypeInLS(response);
                    this.countries = response.values;
                }
            );
        } else {
            this.countries = countries;
        }
    }

    onValueChanged(data?: any) {
        if (!this.bankInstitutionForm) {
            return;
        }
        FormErrors('bankInstitutionForm', this);
    }

    ngOnInit() {
        this.createForm();
        this.getAddressTypes();
        this.getCountries();
        this.updateBankInstitutionForm();
    }

    onSubmit({ value, valid }) {
        const msgSuccess = this.action == 'Add' ? MessageService.ADD_BANK_INSTITUTION_SUCCESS : MessageService.EDIT_BANK_INSTITUTION_SUCCESS;
        const msgFailure = this.action == 'Add' ? MessageService.ADD_BANK_INSTITUTION_FAILURE : MessageService.EDIT_BANK_INSTITUTION_FAILURE;

        const bankInstitutionModel = new BankInstitution();
        bankInstitutionModel.bankId = this.bankInstitutionId;
        bankInstitutionModel.bankName = value.bankName;
        bankInstitutionModel.bankCode = value.bankCode;

        bankInstitutionModel.bankAddress = new Address();
        bankInstitutionModel.bankAddress.id = (this.bankAddress) ? this.bankAddress.id : null;
        bankInstitutionModel.bankAddress.houseNumber = value.houseNumber;
        bankInstitutionModel.bankAddress.street = value.address;
        bankInstitutionModel.bankAddress.zip = value.zipcode;
        bankInstitutionModel.bankAddress.city = value.city;
        bankInstitutionModel.bankAddress.country = value.country;
        bankInstitutionModel.bankAddress.addressType = value.addressType;

        this.savingBankInstitution = true;
        this.financeService.saveBankInstitution(bankInstitutionModel)
            .subscribe(response => {
                this.savingBankInstitution = false;
                if (response.flagResult) {
                    this.dialogRef.close(response);
                }
                this.trackingService.emitEvent(
                    TrackingConstants.BANK_INSTITUTIONS,
                    TrackingConstants.SAVE_BANK_INSTITUTION,
                    TrackingConstants.SUCCESS, 1);
                this.callSnackBarService(response, msgSuccess, msgFailure);
            },
            err => {
                this.savingBankInstitution = false;
                this.trackingService.emitEvent(
                    TrackingConstants.BANK_INSTITUTIONS,
                    TrackingConstants.SAVE_BANK_INSTITUTION,
                    TrackingConstants.FAILURE, 1);
                this.callSnackBarService(err, msgSuccess, msgFailure);
            });
    }

    updateBankInstitutionForm(): void {
        if (this.selectedBankInstitution) {
            for (let inner in this.bankInstitutionForm.controls) {
                this.bankInstitutionForm.get(inner).markAsTouched();
                this.bankInstitutionForm.get(inner).updateValueAndValidity();
            }
            this.bankInstitutionId = this.selectedBankInstitution.bankId;
            this.bankAccounts = this.selectedBankInstitution.bankAccounts;
            this.bankAddress = this.selectedBankInstitution.bankAddress;

            this.bankInstitutionForm.patchValue({
                bankName: this.selectedBankInstitution.bankName,
                bankCode: this.selectedBankInstitution.bankCode,
            });

            if (this.bankAddress) {
                this.bankInstitutionForm.patchValue({
                    houseNumber: this.bankAddress.houseNumber,
                    address: this.bankAddress.street,
                    zipcode: this.bankAddress.zip,
                    city: this.bankAddress.city,
                    country: this.bankAddress.country,
                    addressType: this.bankAddress.addressType,
                });
            }
        }
    }
}


// WEBPACK FOOTER //
// ./src/app/finance/bankinstitutions/bankinstitution/bankinstitution.component.ts